/**
 * DriveZone — Auth Store (Zustand)
 *
 * Holds the current authentication state for the entire app.
 *
 * tempToken:
 *   Exists only between login Step 1 and Step 2 (the OTP screen).
 *   Stored in memory only — never in localStorage or sessionStorage.
 *   Cleared immediately after verify-otp succeeds or on any navigation
 *   away from the OTP page.
 *
 * user:
 *   Set after successful verify-otp. Cleared on logout.
 *
 * isAuthenticated:
 *   Derived from whether user is non-null. Used by AuthGuard.
 */

import { create } from 'zustand'

// ── Types ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: number
  email: string
  is_email_verified: boolean
  subscription_status: 'free' | 'active' | 'cancelled' | 'past_due'
  free_searches_used: number
  free_searches_remaining: number
  can_search: boolean
  is_subscribed: boolean
  referral_code: string
  referral_searches_earned: number
}

interface AuthState {
  // ── State ──────────────────────────────────────────────────────────────
  user: AuthUser | null
  isAuthenticated: boolean

  // Temporary token from login Step 1 — lives only during the OTP step
  // Never persisted — if the user refreshes the page it is lost
  // (they will need to log in again from the start)
  tempToken: string | null

  // Whether the auth state has been initialised by calling /api/auth/me/
  // Used by AuthGuard to distinguish "not yet checked" from "definitely logged out"
  isInitialised: boolean

  // ── Actions ────────────────────────────────────────────────────────────
  setUser: (user: AuthUser) => void
  setTempToken: (token: string) => void
  clearTempToken: () => void
  logout: () => void
  setInitialised: () => void
  updateUser: (updates: Partial<AuthUser>) => void
}

// ── Store ────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>((set) => ({
  // Initial state — not yet authenticated, not yet initialised
  user: null,
  isAuthenticated: false,
  tempToken: null,
  isInitialised: false,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: true,
      isInitialised: true,
      tempToken: null,
    }),

  setTempToken: (token) =>
    set({ tempToken: token }),

  clearTempToken: () =>
    set({ tempToken: null }),

  logout: () =>
    set({
      user: null,
      isAuthenticated: false,
      tempToken: null,
    }),

  setInitialised: () =>
    set({ isInitialised: true }),

  // Partial update — used after a search to update free_searches_used
  updateUser: (updates) =>
    set((state) => ({
      user: state.user ? { ...state.user, ...updates } : null,
    })),
}))
